import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { IState } from '../../redux/redux';
import { ICustomSlide, ISlideInfo, ISlideShowItem, ISlideShowResponse } from './SlideShowTypes';
import Button from '../_Core/Button';
import Modal from '../Modal/Modal';
import StoreX from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { toast } from 'react-toastify';
import Slide from './Slide';
import ConfigFormLanding from '../Setup/ConfigForm/ConfigFormLanding';
import Icon, { IconType } from '../Icon/Icon';
import CustomSlidesEditor from './CustomSlidesEditor';
import { buildSlides, slideDurationSeconds } from './buildSlides';

interface IProps extends IState {}

const SlideShow = (props: IProps) => {
  const kiosk = !!StoreX.GetQueryStringValue('kiosk');
  const recapMode = !!StoreX.GetQueryStringValue('recap');
  const slideShowType = window.location.pathname.split('/').pop() || '';

  const [ready, setReady] = useState(false);
  const [data, setData] = useState<ISlideShowResponse>();
  const [slideData, setSlideData] = useState<ISlideShowItem>();
  const [customSlides, setCustomSlides] = useState<ICustomSlide[]>([]);
  const [slides, setSlides] = useState<ISlideInfo[]>([]);
  const [activeSlide, setActiveSlide] = useState(kiosk ? 0 : -1);
  const [revealed, setRevealed] = useState(false);
  const [paused, setPaused] = useState(false);
  const [autoplay, setAutoplay] = useState(kiosk);
  const [showManage, setShowManage] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [chromeOn, setChromeOn] = useState(true);

  const activeRef = useRef(activeSlide);
  const slidesRef = useRef(slides);
  const revealedRef = useRef(revealed);
  const pausedRef = useRef(paused);
  const autoplayRef = useRef(autoplay);
  const kioskRef = useRef(kiosk);
  const keyRef = useRef<string>();
  const hideChromeTimer = useRef<number>();

  activeRef.current = activeSlide;
  slidesRef.current = slides;
  revealedRef.current = revealed;
  pausedRef.current = paused;
  autoplayRef.current = autoplay;
  kioskRef.current = kiosk;

  const fairName = data?.fairName || props.SettingsResponse?.Name;
  const logoUrl = data?.logo || props.SettingsResponse?.Logo;
  const recapUrl = !recapMode && slideData?.PublicId ? StoreX.BuildUrlWithFair(`/app/SlideShow/${slideData.PublicId}?recap=1`) : '';
  const presenting = activeSlide >= 0 && !recapMode;

  const applyPayload = (value: ISlideShowResponse, keepPlace: boolean) => {
    const item = value.data?.[0];
    setData(value);
    setSlideData(item);
    setCustomSlides(item?.CustomSlides || []);
    const next = buildSlides(
      { ...item, CustomSlides: item?.CustomSlides || [] },
      value,
      {
        fairName: value.fairName || props.SettingsResponse?.Name,
        logoUrl: value.logo || props.SettingsResponse?.Logo,
        recapUrl: !recapMode && item?.PublicId ? StoreX.BuildUrlWithFair(`/app/SlideShow/${item.PublicId}?recap=1`) : '',
      }
    );
    setSlides(next);
    if (keepPlace && keyRef.current) {
      const idx = next.findIndex((s) => s.Key === keyRef.current);
      if (idx >= 0) {
        setActiveSlide(idx);
        activeRef.current = idx;
      }
    }
    setReady(true);
  };

  const init = (quiet?: boolean) => {
    const url = recapMode ? `../Important/Recap` : `../Important/data`;
    const payload = recapMode ? { subType: slideShowType } : { type: 'SlideShow', subType: slideShowType };
    const req = quiet
      ? StoreX.instance.server.postApiQuite<ServerResponse<ISlideShowResponse>>(url, payload)
      : StoreX.instance.server.postApi<ServerResponse<ISlideShowResponse>>(url, payload);

    req.then((x) => {
      if (x.Success) {
        applyPayload(x.Value, !!quiet);
      } else if (!quiet) {
        toast.error(x.Message, { autoClose: false });
      }
    });
  };

  useEffect(() => {
    if (recapMode) {
      init();
      return;
    }
    if (props.User && !ready) init();
  }, [props.User]);

  useEffect(() => {
    if (!slideData) return;
    const next = buildSlides(
      { ...slideData, CustomSlides: customSlides },
      data,
      { fairName, logoUrl, recapUrl }
    );
    setSlides(next);
  }, [slideData, data, customSlides, fairName, logoUrl, recapUrl]);

  useEffect(() => {
    if (activeSlide >= 0) {
      keyRef.current = slides[activeSlide]?.Key;
      setRevealed(!(slides[activeSlide]?.NeedsReveal));
      document.body.classList.add('ss-live');
    } else {
      document.body.classList.remove('ss-live');
    }
    return () => document.body.classList.remove('ss-live');
  }, [activeSlide, slides]);

  const goTo = (index: number) => {
    const max = slidesRef.current.length - 1;
    if (max < 0) return;
    if (index < 0) {
      if (kioskRef.current) {
        setActiveSlide(max);
        return;
      }
      exitShow();
      return;
    }
    if (index > max) {
      if (kioskRef.current || autoplayRef.current) {
        setActiveSlide(0);
        return;
      }
      exitShow();
      return;
    }
    setActiveSlide(index);
  };

  const next = () => {
    const slide = slidesRef.current[activeRef.current];
    if (slide?.NeedsReveal && !revealedRef.current) {
      setRevealed(true);
      return;
    }
    goTo(activeRef.current + 1);
  };

  const prev = () => goTo(activeRef.current - 1);

  const exitShow = () => {
    if (kioskRef.current) return;
    setActiveSlide(-1);
    setAutoplay(false);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
  };

  const startAt = (index: number, play?: boolean) => {
    setActiveSlide(index);
    setAutoplay(!!play || kiosk);
    setPaused(false);
    const el = document.documentElement as HTMLElement & { requestFullscreen?: () => Promise<void> };
    if (el.requestFullscreen) el.requestFullscreen().catch(() => undefined);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (recapMode) return;
      if (activeRef.current < 0) return;
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      switch (event.code) {
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          event.preventDefault();
          prev();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
        case 'PageDown':
        case 'Enter':
        case 'Space':
        case 'NumpadEnter':
          event.preventDefault();
          next();
          break;
        case 'Escape':
        case 'KeyX':
          event.preventDefault();
          exitShow();
          break;
        case 'KeyF':
          event.preventDefault();
          if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
          else document.documentElement.requestFullscreen().catch(() => undefined);
          break;
        case 'KeyP':
          event.preventDefault();
          setPaused((p) => !p);
          break;
        case 'KeyR':
          event.preventDefault();
          init(true);
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [recapMode]);

  useEffect(() => {
    if (!presenting || paused || !autoplay) return;
    const slide = slides[activeSlide];
    const seconds = slideDurationSeconds(slide, slideData?.AutoAdvanceSeconds, kiosk);
    if (!seconds) return;
    const total = seconds * 1000;
    const timers: number[] = [];
    if (slide?.NeedsReveal) {
      timers.push(window.setTimeout(() => setRevealed(true), Math.round(total * 0.4)));
    }
    timers.push(
      window.setTimeout(() => {
        goTo(activeRef.current + 1);
      }, total)
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [presenting, paused, autoplay, activeSlide, slides, slideData, kiosk]);

  useEffect(() => {
    if (!presenting && !kiosk) return;
    const id = window.setInterval(() => init(true), 20000);
    return () => window.clearInterval(id);
  }, [presenting, kiosk]);

  useEffect(() => {
    if (!kiosk || !ready || activeSlide < 0) return;
    setAutoplay(true);
    setChromeOn(true);
    if (hideChromeTimer.current) window.clearTimeout(hideChromeTimer.current);
    hideChromeTimer.current = window.setTimeout(() => setChromeOn(false), 2500);
  }, [kiosk, ready]);

  const bumpChrome = () => {
    setChromeOn(true);
    if (hideChromeTimer.current) window.clearTimeout(hideChromeTimer.current);
    hideChromeTimer.current = window.setTimeout(() => setChromeOn(false), 2500);
  };

  const saveCustom = () => {
    if (!slideData?.PublicId) return;
    const request = {
      Id: slideData.PublicId,
      Type: 'SlideShows',
      Values: { CustomSlides: [JSON.stringify(customSlides)] },
    };
    StoreX.instance.server.postApi<ServerResponse<{ Failed?: boolean; Message?: string }>>(`../ConfigForm/SaveForm`, { data: JSON.stringify(request) }).then((x) => {
      if (x.Success && !x.Value?.Failed) {
        toast.success('Custom slides saved.');
        setSlideData({ ...slideData, CustomSlides: customSlides });
      } else {
        toast.error(x.Value?.Message || x.Message, { autoClose: false });
      }
    });
  };

  const openKiosk = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('kiosk', '1');
    url.searchParams.set('hlo', '1');
    url.searchParams.delete('recap');
    window.open(url.toString(), '_blank');
  };

  const openRecap = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('recap', '1');
    url.searchParams.delete('kiosk');
    window.open(url.toString(), '_blank');
  };

  const current = slides[activeSlide];
  const peek = slides[activeSlide + 1];

  if (!ready) {
    return (
      <div className="main-body-page ss-page" data-testid="slideshow-loading">
        <h3>Loading Slide Show</h3>
      </div>
    );
  }

  if (recapMode) {
    const winners = slides.filter((s) => s.Kind === 'winner' || s.Kind === 'winners').flatMap((s) => s.Winners || []);
    const sponsors = data?.sponsors || [];
    const judges = data?.judges || [];
    const volunteers = data?.volunteers || [];
    return (
      <div className="main-body-page ss-page ss-recap" data-testid="slideshow-recap">
        <div className="ss-recap-actions no-print">
          <Button type="print" text="Print / Save PDF" onClick={() => window.print()} />
        </div>
        {logoUrl && <img src={logoUrl} alt="" className="ss-recap-logo" />}
        <h1>{slideData?.Title || fairName}</h1>
        {slideData?.Description && <p>{slideData.Description}</p>}
        <h2>Winners</h2>
        {winners.length === 0 && <p>No awards have been assigned yet.</p>}
        {winners.map((w, i) => (
          <div key={i} className="ss-recap-winner">
            <div className="ss-recap-award">{w.AwardSubType}</div>
            <strong>
              {w.ProjectId} — {w.Title}
            </strong>
            {w.Owners && <div>{w.Owners}</div>}
            <div>
              {[w.School, w.Division, w.Category].filter(Boolean).join(' · ')}
            </div>
            {(w.PresentedBy || w.Sponsor) && (
              <div>
                {w.PresentedBy ? `Presented by ${w.PresentedBy}` : ''}
                {w.PresentedBy && w.Sponsor ? ' · ' : ''}
                {w.Sponsor}
              </div>
            )}
          </div>
        ))}
        {sponsors.length > 0 && (
          <>
            <h2>Sponsors</h2>
            {sponsors.map((s, i) => (
              <div key={i}>{s.name}</div>
            ))}
          </>
        )}
        {judges.length > 0 && (
          <>
            <h2>Judges</h2>
            <p>{judges.map((j) => j.name).join(', ')}</p>
          </>
        )}
        {volunteers.length > 0 && (
          <>
            <h2>Volunteers</h2>
            <p>{volunteers.map((j) => j.name).join(', ')}</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="main-body-page ss-page" data-testid="slideshow-page">
      {!kiosk && (
        <>
          <div className="ss-toolbar">
            <div>
              <h3>Slide Viewer</h3>
              <p className="ss-help">
                {slides.length} slides · 16:9. Start the show, then use arrow keys or click to advance.
                Winner names wait for a second click. <kbd>Esc</kbd> exits, <kbd>F</kbd> fullscreen, <kbd>P</kbd> pauses auto-play.
              </p>
            </div>
            <div className="ss-toolbar-actions">
              <Button type="refresh" text="Refresh" onClick={() => init()} />
              <Button type="edit" text="Manage Shows" onClick={() => setShowManage(true)} />
              <Button type="new" text="Custom slides" onClick={() => setShowCustom((v) => !v)} />
              <button type="button" className="btn btn-default" onClick={openKiosk}>
                <Icon type={IconType.expand} /> Lobby / kiosk
              </button>
              <button type="button" className="btn btn-default" onClick={openRecap}>
                <Icon type={IconType.qrCode} /> Recap
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => startAt(0, false)}>
                <Icon type={IconType.start} /> Start
              </button>
              <button type="button" className="btn btn-primary" onClick={() => startAt(0, true)}>
                <Icon type={IconType.start} /> Auto-play
              </button>
            </div>
          </div>

          {showCustom && (
            <CustomSlidesEditor
              slides={customSlides}
              onChange={setCustomSlides}
              onSave={saveCustom}
            />
          )}

          <div className="ss-deck">
            {slides.map((x, i) => (
              <div key={x.Key} className="ss-thumb-wrap" onClick={() => startAt(i, false)} role="button" tabIndex={0}>
                <Slide slide={x} thumb revealed />
                <div className="ss-thumb-label">
                  {i + 1}. {x.Title || x.Kind}
                  {x.SubTitle ? ` — ${x.SubTitle}` : ''}
                </div>
              </div>
            ))}
            {slides.length === 0 && <div>No slides to show.</div>}
          </div>
        </>
      )}

      {presenting && current && (
        <div
          className={`ss-stage ${!chromeOn && kiosk ? 'ss-chrome-hidden' : ''}`}
          onMouseMove={bumpChrome}
          onClick={next}>
          <div className="ss-progress">
            <span style={{ width: `${((activeSlide + 1) / Math.max(slides.length, 1)) * 100}%` }} />
          </div>
          <Slide slide={current} active revealed={revealed} />
          <div className="ss-chrome" onClick={(e) => e.stopPropagation()}>
            <div className="ss-chrome-btns">
              <button type="button" onClick={prev}>
                <Icon type={IconType.back} /> Prev
              </button>
              <button
                type="button"
                onClick={() => {
                  const nextPlay = !(autoplay && !paused);
                  setAutoplay(true);
                  setPaused(!nextPlay);
                }}>
                <Icon type={autoplay && !paused ? IconType.pause : IconType.start} /> {autoplay && !paused ? 'Pause' : 'Play'}
              </button>
              <button type="button" onClick={next}>
                Next <Icon type={IconType.next} />
              </button>
              {!kiosk && (
                <button type="button" onClick={exitShow}>
                  <Icon type={IconType.close} /> Exit
                </button>
              )}
            </div>
            <div className="ss-count">
              {activeSlide + 1} / {slides.length}
            </div>
            <div className="ss-next-peek">{peek ? `Next: ${peek.Title || peek.Kind}${peek.SubTitle ? ` — ${peek.SubTitle}` : ''}` : kiosk ? 'Next: loop' : 'Last slide'}</div>
          </div>
        </div>
      )}

      {showManage && (
        <Modal size="xl" title="Manage Slide Shows" setModalOpen={setShowManage}>
          <ConfigFormLanding type="SlideShows" onUpdate={() => init()} />
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return { ...state.main };
};

export default connect(mapStateToProps)(SlideShow);
